/**
 * Client-side prediction + reconciliation for the LOCAL avatar inside the captain's HOUSE (Onda 8) — the
 * rectangular twin of portprediction.ts. The interior is a HOUSE.WIDTH×HOUSE.DEPTH rectangle (not the
 * square plaza), so it steps the same deterministic `stepWalkerBody` but clamps with `collideRoom` (the
 * rectangular collide) against the shell obstacles + placed solid furniture. The walker snapshot wire is
 * IDENTICAL to the port (S_PORTSNAP / PortSelf), so reconcile mirrors PortPrediction exactly.
 */
import { INPUT_HZ, HOUSE, HOUSE_SHELL, WALK, type PortObstacle, type PortSelf } from '@piratas/protocol';
import { collideRoom, makeWalkerBody, stepWalkerBody, type WalkerBody, type WalkerInput } from '@piratas/sim';

const INPUT_DT = 1 / INPUT_HZ;

export class HousePrediction {
  body: WalkerBody = makeWalkerBody(HOUSE_SHELL.spawn.x, HOUSE_SHELL.spawn.y, HOUSE_SHELL.spawn.heading);
  hasBaseline = false;
  /** Shell posts + every placed SOLID furniture piece (seeded by main from S_HOUSE_STATE). Must match the
   *  server HouseRoom's obstacle set so predicted collision == authority. Defaults to the bare shell. */
  private obstacles: readonly PortObstacle[] = HOUSE_SHELL.obstacles;
  private pending: { seq: number; input: WalkerInput }[] = [];

  /** Point prediction at the current interior collision set (shell posts + placed solids). */
  setObstacles(obstacles: readonly PortObstacle[]): void {
    this.obstacles = obstacles;
  }

  /** Apply one fresh local input step (called at INPUT_HZ) and remember it. */
  applyInput(seq: number, input: WalkerInput): void {
    if (!this.hasBaseline) return;
    stepWalkerBody(this.body, input, INPUT_DT);
    collideRoom(this.body, this.obstacles, WALK.RADIUS, HOUSE.WIDTH, HOUSE.DEPTH, HOUSE_SHELL.solidRects);
    this.pending.push({ seq, input: { ...input } });
    if (this.pending.length > 256) this.pending.shift();
  }

  /** Reconcile against the authoritative self-avatar from the latest port snapshot (house uses S_PORTSNAP). */
  reconcile(auth: PortSelf, ackSeq: number): void {
    this.body.x = auth.x;
    this.body.y = auth.y;
    this.body.heading = auth.heading;
    this.body.vx = auth.vx;
    this.body.vy = auth.vy;
    this.pending = this.pending.filter((p) => p.seq > ackSeq);
    for (const p of this.pending) {
      stepWalkerBody(this.body, p.input, INPUT_DT);
      collideRoom(this.body, this.obstacles, WALK.RADIUS, HOUSE.WIDTH, HOUSE.DEPTH, HOUSE_SHELL.solidRects);
    }
    this.hasBaseline = true;
  }

  /** Initialize the predicted avatar directly from an authoritative state. */
  initFrom(auth: PortSelf): void {
    this.body.x = auth.x; this.body.y = auth.y; this.body.heading = auth.heading;
    this.body.vx = auth.vx; this.body.vy = auth.vy;
    this.pending.length = 0;
    this.hasBaseline = true;
  }
}
